import { WarehouseResponseWrap } from '@gate31/types/warehouse';
import { OrderStatus } from '@gate31/types/order-statuses';
import { ImshopItem } from '@gate31/types/cart';
import { WishlistItem } from '@gate31/types/wishlist';
import { stringify } from '../libs/query-string';

export const HOST_GATE31_API = 'https://api.aerogate31.com/gates/';
export const HOST_GATE31_API_V2 = 'https://api.aerogate31.com/v2';

class Gate31ApiError extends Error { name = 'ApiGate31Error' }

interface params {
    [key: string]: string;
}
interface body {
    // eslint-disable-next-line
    [key: string]: body | any;
}
interface headers {
    'Content-Type'?: string;
    Authorization?: string;
}

export interface UpdateAccountBodyInt {
    userId: number;
    userName?: string;
    userSurname?: string;
    userPatronymic?: string;
    userPhone: string;
    userEmail: string;
    userDateOfBirthday?: string;
    passwordInfo?: {
        isUpdate: boolean;
        newPassword: string;
        newPasswordConfirmation: string;
    };
    userFavorites?: string;
}
export interface UpdateAccountResponseErrorInt {
    error: {
       code: string;
       message: string;
    };
}
export interface UpdateAccountResponseInt {
    response: 'ok';
}

interface CreateAccountBody {
    email: string;
    phone: string;
    dateOfBirth: string | null;
    name: {
        firstName : string;
        lastName: string | null;
    };
    password: string | null;
    endpoint: {
        shop: string;
    };
    subscriptions: {
        email: boolean;
        phone: boolean;
    };
}

export interface HistoryResponseInt {
    level: {
        nextLevelBalance: number;
        nextLevelName: string;
    };
    history: {
        changes: {earned: number; spent: number};
        date: string;
        id: number;
        name: string;
    }[];
}

export class Gate31ApiV2 {
    static headers = {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json'
    }

    static updateRemoteWishlist(userId: number, wishlist: WishlistItem[]) {
        const sortedWishlist = wishlist.sort((a, b) => b.syncedOn - a.syncedOn);

        return Gate31ApiV2.post<any>({ userId, wishlist: sortedWishlist }, '/imshop/sync/wishlist');
    }

    static updateCartFields(userId: number, cart: ImshopItem[], lastUpdate: number) {
        return Gate31ApiV2.post<any>({ userId, cart, lastUpdate }, '/imshop/account/update-cart-fields');
    }

    static promoCodeActivation(body: { code: string; phone: string; name: string; lastName: string; email: string }) {
        return Gate31ApiV2.post<any>(body, '/maxma/promo-code-activation');
    }

    static configPhoneSendCode({ phone, code }: { phone: string; code: string }) {
        return Gate31ApiV2.post<any>({ phoneNumber: phone, code }, '/user/confirm-phone/send-code');
    }

    static configPhoneGetCode(phone: string) {
        return Gate31ApiV2.post<any>({ phoneNumber: phone }, '/user/confirm-phone/get-code');
    }

    static createAccount(body: CreateAccountBody) {
        return Gate31ApiV2.post<any>(body, '/user/create-user-process');
    }

    static calculatePurchase(body: any) {
        return Gate31ApiV2.post<any>(body, '/programm-loyalty/calculate-purchase')
            .then(response => {
                return response;
            });
    }

    static getBalanceHistory(phone: string) {
        return Gate31ApiV2.get<HistoryResponseInt>({ phoneNamuber: phone }, '/programm-loyalty/get-banace-history', {});
    }

    static loyaltyGetUser(phone: string) {
        return Gate31ApiV2.get<{
            customer: any;
            retailOrderStatistics: any;
            status: any;
            balances: {
                available: number;
                blocked: number;
                systemName: string;
            }[];
            customerSegmentations: {
                segmentation: { name: string };
                segment: { name: string };
            }[];
        }>({ phone }, '/programm-loyalty/get-balance', {});
    }

    private static post<T>(body: body, path: string): Promise<T> {
        const url = `${HOST_GATE31_API_V2}${path}`;

        return fetch(url, {
            method: 'POST',
            body: JSON.stringify(body),
            headers: { ...Gate31Api.headers }
        }).then(res => {
            //TEMPORARY
            if (res.url.includes('update-cart') || res.url.includes('wishlist')) return null as T;
            return res.json() as T;
        }).catch(error => {
            throw new Gate31ApiError(`Ошибка в обращении к ApiGate - ${url} - ${error}`);
        });
    }

    private static get<T>(params: params | null, path: string, headers: headers | null): Promise<T> {
        const url = `${HOST_GATE31_API_V2}${path}?${stringify({ ...params }, null)}`;

        return fetch(url, {
            method: 'GET',
            headers: { ...headers, ...Gate31Api.headers }
        })
            .then(response => response.json())
            .then((response: T) => {
                return response;
            })
            .catch(err => {
                throw new Gate31ApiError(`Ошибка в обращении к ApiGate - ${url} - ${err}`);
            });
    }
}

export default class Gate31Api {
    static headers = {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json'
    }

    static getCustomStatus(): Promise<{ response: Array<OrderStatus> }> {
        return fetch(`${HOST_GATE31_API}insales-api/custom-statuses`, {
            method: 'GET',
            headers: Gate31Api.headers
        })
            .then(response => response.json())
            .catch(err => {
                throw new Gate31ApiError(`Ошибка в обращении к ApiGate - insales-api/custom-statuses - ${err}`);
            });
    }

    static getWarehouseStock(id: string) {
        const params = { id };

        return Gate31Api.get(params, 'warehouse/new-stock', null);
    }

    static updateAccount(body: UpdateAccountBodyInt): Promise<UpdateAccountResponseInt | UpdateAccountResponseErrorInt> {
        return Gate31Api.post(body, 'update-user/update-user')
            .then(response => {
                return response.json();
            })
            .catch(error => { throw error; });
    }

    static createAccount(body: CreateAccountBody) {
        return Gate31Api.post(body, 'create-user/create-user')
            .then(response => response.json());
    }

    static post(body: body, path: string) {
        const url = `${HOST_GATE31_API}${path}`;

        return fetch(url, {
            method: 'POST',
            body: JSON.stringify(body),
            headers: { ...Gate31Api.headers }
        }).then(res => {
            return res;
        }).catch(error => {
            throw new Gate31ApiError(`Ошибка в обращении к ApiGate - ${url} - ${error}`);
        });
    }

    static get(params: params | null, path: string, headers: headers | null): Promise<WarehouseResponseWrap> {
        const url = `${HOST_GATE31_API}${path}?${stringify({ ...params }, null)}`;

        return fetch(url, {
            method: 'GET',
            headers: { ...headers, ...Gate31Api.headers }
        })
            .then(response => response.json())
            .then((response: WarehouseResponseWrap) => {
                return response;
            })
            .catch(err => {
                throw new Gate31ApiError(`Ошибка в обращении к ApiGate - ${url} - ${err}`);
            });
    }
}
